import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Users, Dumbbell, CalendarDays, TrendingUp, CreditCard, Palette, ArrowRight, Quote, Check, Zap, Menu, X, LayoutDashboard, MessageSquare, Flame } from "lucide-react";
import { SPORT_THEMES, THEME_KEYS, THEME_EMOJI, SPOTTR_URL, onAccent } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import "@/landing.css";

const FEATURES = [
  [Users, "Client Management", "Profiles, goals, injuries, photos and notes — every client in one tidy place."],
  [Dumbbell, "Program Builder", "Build multi-day programs from your exercise library and assign them in a tap."],
  [CalendarDays, "Session Scheduling", "Availability, block-outs, one-tap confirmations and automatic reminders."],
  [TrendingUp, "Progress Tracking", "Weekly reports, PR highlights, check-ins, streaks and progress photos."],
  [CreditCard, "Payments & Invoicing", "Packs, memberships, invoices and reminders so you get paid on time."],
  [Palette, "White-label Portal", "Your name, your logo, your sport theme — your clients never see ours."],
];

const TESTIMONIALS = [
  ["Maya Chen", "Pilates · Studio owner", "🤸", "My clients book, confirm and check in without a single WhatsApp back-and-forth. Sunday admin went from three hours to zero."],
  ["Darius Okafor", "Strength coach", "🏋️", "The programs and tick-offs changed how my clients train between sessions. Adherence is up and I can actually see it."],
  ["Lena Fischer", "Running coach", "🏃", "It looks like my brand, not a generic app. The orange-and-black running theme sold two clients on its own."],
];

const NAV_LINKS = [["#features", "Features", "features"], ["#themes", "Themes", "themes"], ["#pricing", "Pricing", "pricing"]];

const PLANS = [
  ["Starter", "Free", "for your first 5 clients", ["Up to 5 clients", "Scheduling & reminders", "Weekly reports", "Powered by SpottrOS"]],
  ["Pro", "29", "per month · up to 40 clients", ["Everything in Starter", "Programs & exercise library", "White-label portal + sport themes", "Voice notes & check-ins"]],
  ["Studio", "79", "per month · unlimited", ["Everything in Pro", "Multiple coaches", "Payments & memberships", "Monthly report cards"]],
];

const STATS = [["500+", "Coaches"], ["10,000+", "Sessions tracked"], ["12", "Sport disciplines"]];

const DEMO_VIDEO_ID = "u4PjES0FcsE";
const DEMO_VIDEO_SRC = `https://www.youtube.com/embed/${DEMO_VIDEO_ID}?rel=0&modestbranding=1&playsinline=1`;

const Logo = ({ testId = "landing-logo" }) => (
  <Link to="/" className="lp-logo" data-testid={testId}><span className="lp-mark"><Zap size={18} strokeWidth={2.8} fill="currentColor" /></span><span className="lp-wordmark">SpottrOS</span></Link>
);

const MOCK_KPIS = [["24", "Active clients"], ["92%", "Adherence"], ["$4.2k", "This month"]];
const MOCK_BARS = [42, 66, 50, 82, 70, 96, 60];
const MOCK_SESSIONS = [["06:30", "Sarah M.", "Strength"], ["08:00", "Dev P.", "Mobility"], ["17:30", "Lena F.", "Intervals"]];
const MOCK_NAV = [LayoutDashboard, CalendarDays, Users, Dumbbell, CreditCard, MessageSquare];

const HeroArt = () => (
  <div className="lp-art lp-rise d3" data-testid="hero-art" aria-hidden="true">
    <div className="lp-glow" />
    <div className="lp-mock" data-testid="hero-mockup">
      <div className="mk-top"><span className="mk-dot" /><span className="mk-dot" /><span className="mk-dot" /><span className="mk-url">app.spottr-os.com/admin</span></div>
      <div className="mk-body">
        <aside className="mk-side">{MOCK_NAV.map((Icon, i) => <span key={i} className={i === 0 ? "on" : ""}><Icon size={15} strokeWidth={2.2} /></span>)}</aside>
        <div className="mk-main">
          <div className="mk-head"><b>Good morning, Coach</b><span className="mk-pill">Tue · 7 sessions</span></div>
          <div className="mk-kpis">{MOCK_KPIS.map(([n, l]) => <div key={l}><b>{n}</b><small>{l}</small></div>)}</div>
          <div className="mk-chart">{MOCK_BARS.map((h, i) => <i key={i} style={{ "--h": `${h}%`, "--d": `${0.5 + i * 0.08}s` }} />)}</div>
          <div className="mk-list">{MOCK_SESSIONS.map(([t, n, f]) => <div key={t}><span className="t">{t}</span><span className="av">{n[0]}</span><span className="n">{n}</span><span className="f">{f}</span><Check size={13} strokeWidth={3} /></div>)}</div>
        </div>
      </div>
    </div>
    <div className="lp-phone" data-testid="hero-phone">
      <div className="ph-top" />
      <div className="ph-card"><small>Next session</small><b>Thu 07:00</b><span className="ph-btn">Confirm</span></div>
      <div className="ph-row"><Flame size={14} strokeWidth={2.4} /><span>Streak · 6 weeks</span></div>
      <div className="ph-bars">{[3, 5, 4, 6, 7].map((h, i) => <i key={i} style={{ height: h * 5 }} />)}</div>
    </div>
  </div>
);

const ThemeDots = ({ rowRef, count }) => {
  const [dot, setDot] = useState(0);
  useEffect(() => {
    const el = rowRef.current; if (!el) return;
    const onScroll = () => { const card = el.querySelector(".lp-jersey"); const step = card ? card.offsetWidth + 16 : 256; setDot(Math.min(count - 1, Math.round(el.scrollLeft / step))); };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [rowRef, count]);
  const go = (i) => { const el = rowRef.current; const card = el?.querySelector(".lp-jersey"); if (el && card) el.scrollTo({ left: i * (card.offsetWidth + 16), behavior: "smooth" }); };
  return (
    <div className="lp-dots" role="tablist" aria-label="Sport themes" data-testid="theme-dots">
      {Array.from({ length: count }, (_, i) => <button type="button" key={i} role="tab" aria-selected={i === dot} className={i === dot ? "on" : ""} onClick={() => go(i)} aria-label={`Theme ${i + 1}`} data-testid={`theme-dot-${i}`} />)}
      <span className="hint" data-testid="theme-swipe-hint">Swipe <ArrowRight size={12} strokeWidth={3} /></span>
    </div>
  );
};

const ThemePeek = ({ k, t }) => {
  const v = t.vars;
  return (
    <div className="lp-peek" style={{ background: v["--bg"], color: v["--ink"], fontFamily: t.font }} data-testid={`landing-theme-preview-${k}`}>
      <div className="pk-top" style={{ background: v["--surface"], borderColor: v["--line"] }}>
        <span className="pk-logo" style={{ background: v["--accent"], color: v["--on-accent"] }}>YS</span>
        <div><b>Your Studio</b><small style={{ color: v["--ink3"] }}>Client portal</small></div>
      </div>
      <div className="pk-body">
        <div className="pk-quote" style={{ borderColor: v["--accent"], background: v["--muted"], color: v["--ink2"] }}>“{t.tagline}”</div>
        <div className="pk-card" style={{ background: v["--surface"], borderColor: v["--line"] }}>
          <small style={{ color: v["--ink3"] }}>Your next session</small><b>Thu 07:00 · 60 min</b>
          <span className="pk-bar" style={{ background: v["--strong"] }}><i style={{ background: v["--accent"] }} /></span>
          <div className="pk-btns"><span style={{ background: v["--accent"], color: v["--on-accent"] }}>Confirm</span><span style={{ borderColor: v["--line"], color: v["--ink"] }}>Change time</span></div>
        </div>
        <small className="pk-pow" style={{ color: v["--ink3"] }}>Powered by SpottrOS</small>
      </div>
    </div>
  );
};

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [menu, setMenu] = useState(false);
  const [peek, setPeek] = useState(null);
  const rowRef = useRef(null);
  return (
    <div className="lp" data-testid="landing-page">
      <header className="lp-nav" data-testid="landing-nav">
        <Logo />
        <nav>
          {NAV_LINKS.map(([href, label, id]) => <a key={id} href={href} className="lp-link" data-testid={`nav-${id}`}>{label}</a>)}
          <Link to="/login" className="lp-link" data-testid="nav-signin">Sign In</Link>
          <Link to="/signup" className="lp-btn sm" data-testid="nav-trial">Start Free Trial</Link>
          <button type="button" className="lp-burger" onClick={() => setMenu((m) => !m)} aria-expanded={menu} aria-label={menu ? "Close menu" : "Open menu"} data-testid="nav-burger">{menu ? <X size={22} /> : <Menu size={22} />}</button>
        </nav>
        {menu && (
          <>
            <div className="lp-menu-bg" onClick={() => setMenu(false)} data-testid="nav-menu-backdrop" />
            <div className="lp-menu" data-testid="nav-menu">
              {NAV_LINKS.map(([href, label, id]) => <a key={id} href={href} onClick={() => setMenu(false)} data-testid={`menu-${id}`}>{label}</a>)}
              <Link to="/login" onClick={() => setMenu(false)} data-testid="menu-signin">Sign In</Link>
              <Link to="/signup" className="lp-btn" onClick={() => setMenu(false)} data-testid="menu-trial">Start Free Trial <ArrowRight size={18} /></Link>
            </div>
          </>
        )}
      </header>

      <section className="lp-hero" data-testid="landing-hero">
        <div>
          <span className="lp-kick lp-rise">Trainer OS · built for coaches</span>
          <h1 className="lp-rise d1">The smarter way to run your <em>fitness business.</em></h1>
          <p className="lp-rise d2">SpottrOS gives personal trainers, coaches and studios everything they need to manage clients, programs, bookings and payments — in one place, under their own brand.</p>
          <div className="lp-actions lp-rise d3">
            <Link to="/signup" className="lp-btn" data-testid="hero-trial">Start Free Trial <ArrowRight size={18} /></Link>
            <a href="#how" className="lp-btn o" data-testid="hero-how">See How It Works</a>
          </div>
          {user && <button className="lp-signed" onClick={() => nav(user.role === "ADMIN" ? "/admin" : "/client")} data-testid="hero-dashboard">You're signed in — go to your {user.role === "ADMIN" ? "dashboard" : "portal"} →</button>}
        </div>
        <HeroArt />
      </section>

      <section className="lp-stats" data-testid="landing-stats">
        <div className="in">
          {STATS.map(([n, l], i) => (
            <Fragment key={l}>
              {i > 0 && <span className="lp-sep" />}
              <div className="lp-stat" data-testid={`stat-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}><span className="lp-num">{n}</span><small>{l}</small></div>
            </Fragment>
          ))}
        </div>
      </section>

      <section id="features" className="lp-sec" data-testid="landing-features">
        <span className="lp-kick">Features</span>
        <h2>Everything a coaching business needs. <em>Nothing it doesn't.</em></h2>
        <p className="lp-sub">Six tools, one login. Built to run a studio from your phone between sessions.</p>
        <div className="lp-grid3">{FEATURES.map(([Icon, name, desc], i) => (
          <div className="lp-feat" key={name} data-testid={`feature-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}><span className="n">0{i + 1}</span><span className="ic"><Icon size={28} strokeWidth={2.2} /></span><b>{name}</b><p>{desc}</p></div>))}
        </div>
      </section>

      <section id="how" className="lp-sec" style={{ paddingTop: 0 }} data-testid="landing-demo">
        <span className="lp-kick">See how it works</span>
        <h2>Two minutes. <em>The whole platform.</em></h2>
        <p className="lp-sub">From adding a client to their first branded weekly report.</p>
        <div className="lp-video" data-testid="landing-demo-video">
          <iframe src={DEMO_VIDEO_SRC} title="SpottrOS — Built For Coaches" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen loading="lazy" data-testid="landing-demo-iframe" />
        </div>
      </section>

      <section id="themes" className="lp-sec" style={{ paddingTop: 0 }} data-testid="landing-themes">
        <span className="lp-kick">Sport themes</span>
        <h2>Built for every discipline.</h2>
        <p className="lp-sub">Pick your jersey. Colours, typography and texture change across your whole client portal. Hover a card to preview the portal.</p>
        <div className="lp-row" ref={rowRef} data-testid="theme-row">{THEME_KEYS.map((k, i) => { const t = SPORT_THEMES[k]; const sw = t.vars["--accent"]; return (
          <div className={`lp-jersey ${peek === k ? "open" : ""}`} key={k} style={{ "--sw": sw, "--on": onAccent(sw) }} onClick={() => setPeek(peek === k ? null : k)} onMouseLeave={() => setPeek((p) => (p === k ? null : p))} data-testid={`landing-theme-${k}`}>
            <div className="sw"><span className="num">{String(i + 1).padStart(2, "0")}</span><span className="ico" aria-hidden="true" data-testid={`landing-theme-icon-${k}`}>{THEME_EMOJI[k]}</span></div>
            <div className="meta"><b>{t.label}</b><small>{t.desc}</small><div className="dots">{["--bg", "--surface", "--accent", "--ink"].map((v) => <i key={v} style={{ background: t.vars[v] }} />)}</div></div>
            <ThemePeek k={k} t={t} />
          </div>); })}
        </div>
        <ThemeDots rowRef={rowRef} count={THEME_KEYS.length} />
      </section>

      <section className="lp-sec" style={{ paddingTop: 0 }} data-testid="landing-testimonials">
        <span className="lp-kick">Coaches on SpottrOS</span>
        <h2>Less admin. <em>More coaching.</em></h2>
        <div className="lp-grid3" style={{ marginTop: 36 }}>{TESTIMONIALS.map(([name, sport, emoji, quote]) => (
          <div className="lp-quote" key={name} data-testid={`testimonial-${name.split(" ")[0].toLowerCase()}`}><Quote size={26} className="q" /><p>“{quote}”</p>
            <div className="lp-who"><span className="lp-ava" data-testid={`testimonial-avatar-${name.split(" ")[0].toLowerCase()}`}>{name.split(" ").map((w) => w[0]).join("")}<em aria-label={sport}>{emoji}</em></span><div><b>{name}</b><small>{sport}</small></div></div>
          </div>))}
        </div>
      </section>

      <section id="pricing" className="lp-sec" style={{ paddingTop: 0 }} data-testid="landing-pricing">
        <span className="lp-kick">Pricing</span>
        <h2>Simple plans that <em>grow with you.</em></h2>
        <div className="lp-grid3" style={{ marginTop: 40 }}>{PLANS.map(([name, price, sub, perks], i) => (
          <div className={`lp-plan ${i === 1 ? "hot" : ""}`} key={name} data-testid={`plan-${name.toLowerCase()}`}>
            {i === 1 && <span className="badge" data-testid="plan-most-popular">Most Popular</span>}
            <b className="name">{name}</b>
            <div className="lp-price">{price === "Free" ? price : <><span className="cur">$</span>{price}</>}</div>
            <small>{sub}</small>
            <ul>{perks.map((p) => <li key={p}><Check size={16} strokeWidth={3} /> {p}</li>)}</ul>
            <Link to={`/signup?plan=${name.toLowerCase()}`} className={`lp-btn ${i === 1 ? "" : "o"}`} data-testid={`plan-signup-${name.toLowerCase()}`}>{i === 1 ? "Start Free Trial" : "Sign Up"}</Link>
          </div>))}
        </div>
      </section>

      <footer className="lp-foot" data-testid="landing-footer">
        <div className="in">
          <div><Logo testId="footer-logo" /><div className="tag" data-testid="footer-tagline">Built for coaches. Powered by SpottrOS.</div></div>
          <nav>
            <a href="#features" data-testid="footer-features">Features</a>
            <a href="#pricing" data-testid="footer-pricing">Pricing</a>
            <Link to="/login" data-testid="footer-signin">Sign In</Link>
            <a href={`${SPOTTR_URL}/terms`} data-testid="footer-terms">Terms</a>
            <a href={`${SPOTTR_URL}/privacy`} data-testid="footer-privacy">Privacy</a>
            <a href="mailto:hello@spottr-os.com" data-testid="footer-contact">Contact</a>
          </nav>
          <div className="legal">© {new Date().getFullYear()} SpottrOS · Built on Emergent</div>
        </div>
      </footer>
    </div>
  );
}
